import React, { useEffect } from 'react';
import { Modal, Form, Select, DatePicker, TimePicker, Input, Row, Col, Button, Tag, Tooltip } from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { ATTENDANCE_STATUSES, calculateTimingStatusClient } from '../attendanceUtils';

const { Option } = Select;

export const parseTimeStringToDayjs = (timeVal, dateVal) => {
  if (!timeVal) return null;
  if (dayjs.isDayjs(timeVal)) return timeVal;

  const baseDateStr = dateVal ? (dayjs.isDayjs(dateVal) ? dateVal.format('YYYY-MM-DD') : dayjs(dateVal).format('YYYY-MM-DD')) : dayjs().format('YYYY-MM-DD');
  const str = String(timeVal).trim();

  if (str.includes('T') || str.includes(' ')) {
    const d = dayjs(str);
    if (d.isValid()) return d;
  }

  const combo = dayjs(`${baseDateStr} ${str}`);
  if (combo.isValid()) return combo;

  const parts = str.split(':');
  if (parts.length >= 2) {
    const hh = parseInt(parts[0], 10);
    const mm = parseInt(parts[1], 10);
    const ss = parseInt(parts[2] || 0, 10);
    if (!isNaN(hh) && !isNaN(mm)) {
      return (dateVal ? (dayjs.isDayjs(dateVal) ? dateVal.clone() : dayjs(dateVal)) : dayjs())
        .hour(hh)
        .minute(mm)
        .second(isNaN(ss) ? 0 : ss);
    }
  }
  return null;
};

export default function AttendanceEntryModal({
  open,
  editingRecord,
  form,
  onSave,
  onCancel,
  isStudio,
  staffList = [],
  candidatesList = [],
  academicYears = [],
  ayFilter = 'all',
  standardFilter = 'all',
  shiftSettings = []
}) {
  useEffect(() => {
    if (open) {
      if (editingRecord) {
        const attDate = editingRecord.attendance_date ? dayjs(editingRecord.attendance_date) : dayjs();
        const personType = editingRecord.person_type || (isStudio ? 'CANDIDATE' : 'STAFF');
        const staffId = editingRecord.staff_id || (personType === 'STAFF' ? editingRecord.id : undefined);
        const candidateId = editingRecord.candidate_id || (personType === 'CANDIDATE' ? editingRecord.id : undefined);

        // Find candidate if candidateId is present
        const candObj = candidateId ? candidatesList.find(c => String(c.candidate_id) === String(candidateId)) : null;

        // Resolve academic year ID: from record, or from candidate, or from ayFilter
        let rawAyId = editingRecord.academic_year_id || candObj?.academic_year_id || (ayFilter !== 'all' ? ayFilter : undefined);
        let resolvedAyId = undefined;
        if (rawAyId !== undefined && rawAyId !== null && rawAyId !== '') {
          const matchedAy = academicYears.find(ay => String(ay.academic_year_id) === String(rawAyId));
          resolvedAyId = matchedAy ? matchedAy.academic_year_id : (Number(rawAyId) || rawAyId);
        }

        form.setFieldsValue({
          person_type: personType,
          staff_id: staffId ? (Number(staffId) || staffId) : undefined,
          candidate_id: candidateId ? (Number(candidateId) || candidateId) : undefined,
          biometric_user_id: editingRecord.biometric_user_id || candObj?.biometric_user_id || undefined,
          academic_year_id: resolvedAyId,
          attendance_date: attDate,
          check_in_time: parseTimeStringToDayjs(editingRecord.check_in_time || editingRecord.check_in, attDate),
          check_out_time: parseTimeStringToDayjs(editingRecord.check_out_time || editingRecord.check_out, attDate),
          status: editingRecord.status && editingRecord.status !== 'NOT_RECORDED' ? editingRecord.status : 'PRESENT',
          remarks: editingRecord.remarks || ''
        });
      } else {
        let defaultAyId = undefined;
        if (ayFilter !== 'all') {
          const matchedAy = academicYears.find(ay => String(ay.academic_year_id) === String(ayFilter));
          defaultAyId = matchedAy ? matchedAy.academic_year_id : (Number(ayFilter) || ayFilter);
        }
        if (defaultAyId !== undefined) {
          form.setFieldsValue({ academic_year_id: defaultAyId });
        }
      }
    } else {
      form.resetFields();
    }
  }, [open, editingRecord, form, isStudio, candidatesList, academicYears, ayFilter]);

  const handleStaffChange = (staffId) => {
    const staff = staffList.find(s => String(s.staff_id) === String(staffId));
    if (staff && staff.biometric_user_id) {
      form.setFieldsValue({ biometric_user_id: staff.biometric_user_id });
    }
  };

  const handleCandidateChange = (candidateId) => {
    const cand = candidatesList.find(c => String(c.candidate_id) === String(candidateId));
    if (cand) {
      const updates = {};
      if (cand.biometric_user_id) updates.biometric_user_id = cand.biometric_user_id;
      if (cand.academic_year_id) {
        const matchedAy = academicYears.find(ay => String(ay.academic_year_id) === String(cand.academic_year_id));
        updates.academic_year_id = matchedAy ? matchedAy.academic_year_id : (Number(cand.academic_year_id) || cand.academic_year_id);
      }
      form.setFieldsValue(updates);
    }
  };

  const handleAutoCalculate = () => {
    const values = form.getFieldsValue();
    const personType = values.person_type || 'STAFF';
    let comb = 'ALL';
    if (personType === 'CANDIDATE' && values.candidate_id) {
      const cand = (candidatesList || []).find(c => c.candidate_id === values.candidate_id);
      if (cand && cand.combination) comb = cand.combination;
    }
    const result = calculateTimingStatusClient(
      values.check_in_time,
      values.check_out_time,
      personType,
      shiftSettings,
      values.attendance_date,
      comb
    );
    if (result && result.status) {
      form.setFieldsValue({ status: result.status });
    }
  };

  return (
    <Modal
      title={editingRecord ? "Set / Edit Individual Time (Date-Wise)" : "Set Individual Time & Attendance (Date-Wise)"}
      open={open}
      onOk={onSave}
      onCancel={onCancel}
      destroyOnClose
      width={600}
    >
      <Form form={form} layout="vertical">
        <Form.Item name="person_type" label="Person Type" rules={[{ required: true, message: 'Please select person type' }]}>
          <Select
            onChange={() => form.setFieldsValue({ staff_id: undefined, candidate_id: undefined })}
            disabled={isStudio}
          >
            {!isStudio && <Option value="STAFF">Staff Member</Option>}
            <Option value="CANDIDATE">Candidate / Student</Option>
          </Select>
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) => prevValues.person_type !== currentValues.person_type}
        >
          {({ getFieldValue }) =>
            getFieldValue('person_type') === 'STAFF' ? (
              <Form.Item name="staff_id" label="Select Staff Member" rules={[{ required: true, message: 'Please select staff member' }]}>
                <Select
                  placeholder="Choose staff member..."
                  showSearch
                  optionFilterProp="children"
                  onChange={handleStaffChange}
                >
                  {staffList.map(s => (
                    <Option key={s.staff_id} value={s.staff_id}>
                      {s.staff_name} ({s.staff_code || `ID: ${s.staff_id}`})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            ) : (
              <div>
                <Form.Item name="candidate_id" label="Select Candidate" rules={[{ required: true, message: 'Please select candidate' }]}>
                  <Select
                    placeholder="Choose candidate..."
                    showSearch
                    optionFilterProp="children"
                    onChange={handleCandidateChange}
                  >
                    {candidatesList
                      .filter(c => {
                        if (editingRecord && (c.candidate_id === editingRecord.candidate_id || c.candidate_id === editingRecord.id)) {
                          return true;
                        }
                        return (ayFilter === 'all' || String(c.academic_year_id) === String(ayFilter)) &&
                               (standardFilter === 'all' || String(c.standard_id) === String(standardFilter));
                      })
                      .map(c => (
                        <Option key={c.candidate_id} value={c.candidate_id}>
                          {c.name} (ID: {c.candidate_id}{c.combination ? ` • Comb: ${c.combination}` : ''})
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </div>
            )
          }
        </Form.Item>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="biometric_user_id" label="Biometric Machine PIN / User ID">
              <Input placeholder="e.g. 101" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="academic_year_id" label="Academic Year">
              <Select placeholder="Select year..." allowClear>
                {academicYears.map(ay => (
                  <Option key={ay.academic_year_id} value={ay.academic_year_id}>
                    {ay.academic_year}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="attendance_date" label="Specific Date" rules={[{ required: true, message: 'Please select date' }]}>
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="status"
              label={
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                  <span>Attendance Status</span>
                  <Tooltip title="Calculate status automatically based on entered In/Out times and shift timings">
                    <Button
                      type="link"
                      size="small"
                      icon={<ThunderboltOutlined />}
                      onClick={handleAutoCalculate}
                      style={{ padding: 0, fontSize: 11 }}
                    >
                      Auto-Calc
                    </Button>
                  </Tooltip>
                </div>
              }
              rules={[{ required: true, message: 'Please select status' }]}
            >
              <Select>
                {ATTENDANCE_STATUSES.map(st => (
                  <Option key={st.value} value={st.value}>
                    <Tag color={st.color} style={{ marginRight: 6 }}>{st.code}</Tag>
                    {st.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="check_in_time" label="Check-In Time (In-Time)">
              <TimePicker style={{ width: '100%' }} format="HH:mm:ss" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="check_out_time" label="Check-Out Time (Out-Time)">
              <TimePicker style={{ width: '100%' }} format="HH:mm:ss" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="remarks" label="Shift Notes / Overtime Remarks">
          <Input.TextArea rows={2} placeholder="Optional notes (e.g. 2 hrs Overtime approved, late permission)..." />
        </Form.Item>
      </Form>
    </Modal>
  );
}
